import { useEffect, useRef, useState } from 'react';
import styles from './Wordmark.module.css';

// "› <Shumunov.Solutions />▍" — a prompt, a namespaced component, a live cursor.
const parts = [
  { text: '› ', kind: 'prompt' },
  { text: '<', kind: 'tag' },
  { text: 'Shumunov', kind: 'name' },
  { text: '.', kind: 'dot' },
  { text: 'Solutions', kind: 'sub' },
  { text: ' />', kind: 'tag' },
];
const TOTAL = parts.reduce((n, p) => n + p.text.length, 0);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Characters typed so far. Typing starts once the element scrolls into view;
 * it jumps straight to the end when typing is off or motion is reduced.
 */
function useTyped(enabled, ref) {
  const [count, setCount] = useState(() => (enabled && !prefersReducedMotion() ? 0 : TOTAL));
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (count >= TOTAL || !node) return;
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && setInView(true), {
      threshold: 0.6,
    });
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- observe once
  }, []);

  useEffect(() => {
    if (!inView || count >= TOTAL) return;
    const timer = setTimeout(() => setCount((c) => c + 1), count === 0 ? 400 : 55);
    return () => clearTimeout(timer);
  }, [inView, count]);

  return count;
}

/**
 * size: 'nav' | 'hero' | 'footer'. typed: types itself out once scrolled into view.
 * compact: drops ".Solutions" (narrow headers).
 */
export function Wordmark({ size = 'nav', typed = false, compact = false, className = '' }) {
  const ref = useRef(null);
  const count = useTyped(typed, ref);
  let offset = 0;

  return (
    <span
      ref={ref}
      className={`${styles.wordmark} ${styles[size]} ${compact ? styles.compact : ''} ${className}`}
      role="img"
      aria-label="Shumunov Solutions"
      data-typing={count < TOTAL || undefined}
    >
      {parts.map(({ text, kind }) => {
        const shown = text.slice(0, Math.max(0, count - offset));
        offset += text.length;
        return shown ? (
          <span key={kind + text} className={styles[kind]} aria-hidden="true">
            {shown}
          </span>
        ) : null;
      })}
      <span className={styles.cursor} aria-hidden="true" />
    </span>
  );
}
